import { Injectable } from '@angular/core';
import { tap } from 'rxjs/operators';
// Store
import { Selector, State, StateContext, Action, Store } from '@ngxs/store';
// Service
import { RuleService } from '../service/rule.service';
// Actions
import {
	SetRulesCheckedRules,
	SetAvailableRuleSet,
	SetIsRunningRules,
	SetOrdersRules,
	UpdatedRuleStatus,
	UpdatedMultipleRuleStatus,
	AddComementRule,
	AddStipRule,
	AddManualRule,
	DeleteComementRule,
	DeleteStipsRule,
	UpdatedRuleOrders,
	RuleComapateInfo,
	SetRuleSet,
	UpdatedRuleViolation,
	UpdatedRulelog,
	CleardRulelog,
	SetUncheckedRules,
	SetSelectedRule,
	DeleteSelectedRule,
	OnRevised,
	AddSrrRules,
	ClearRules
} from './action/rules.actions';
import { Logout } from '@cv-invision/core';
// Models
import { RuleStateModel, RuleSaveStatus, OrderCI_RULES_STATE_PATH } from './model/rules-state.model';
import { DOCUMENT_RULES_UI_SECTIONS } from '../../model/quality.constant';
import { RULE_STATUS, RULES_INDICATOR } from '../model/constants';
import { OrdersRulesModel } from '../model/ci-rule.model';

@State<RuleStateModel>({
	name: OrderCI_RULES_STATE_PATH,
	defaults: new RuleStateModel()
})
@Injectable()
export class RulesState {
	constructor(private rulesService: RuleService, private store: Store) {}

	/**
	 * Get the value from rules can save state.
	 * @param state: QualityStateModel
	 */
	@Selector()
	static getRulesCanSaveSate(state: RuleStateModel): RuleSaveStatus[] {
		return state.ruleSaveStatus;
	}

	/**
	 * Get the value from Available RuleSet.
	 * @param state: QualityStateModel
	 */
	@Selector()
	static getAvailableRuleSet(state: RuleStateModel): RuleSaveStatus[] {
		return state.availableRuleSet;
	}

	/**
	 * Get the value from  RuleSet.
	 * @param state: QualityStateModel
	 */
	@Selector()
	static getRuleSet(state: RuleStateModel): any[] {
		return state.rulesSet;
	}

	/**
	 * Get the values from rules compare info .
	 * @param state: QualityStateModel
	 */
	@Selector()
	static getRuleCompareInfo(state: RuleStateModel): string[] {
		return state.rulesCompareInfo;
	}

	/**
	 * Get the value from Available RuleSet.
	 * @param state
	 */
	@Selector()
	static getOrderRules(state: RuleStateModel): any {
		return state.ordersRules;
	}

	/**
	 * Get the value from rulesLog.
	 * @param state: RuleStateModel
	 */
	@Selector()
	static getRulesLog(state: RuleStateModel): any[] {
		return state.rulesLog;
	}

	/**
	 * Get the value from uncheckedRules.
	 * @param state: RuleStateModel
	 */
	@Selector()
	static getUnchekedRules(state: RuleStateModel): boolean {
		return state.uncheckedRules;
	}

	/**
	 * Get the value from the last Selected Rule.
	 * @param state: RuleStateModel.selectedRule
	 */
	@Selector()
	static getSelectedRule(state: RuleStateModel): boolean {
		return state.selectedRule;
	}

	/**
	 * Get the value whether or not the current section is revised.
	 * @param state: RuleStateModel.selectedRule
	 */
	@Selector()
	static getSelectedOnRevised(state: RuleStateModel): boolean {
		return state.onRevised;
	}

	/**
	 * set the rule set Available for the document
	 * @param ctx
	 * @param rulesSets
	 */
	@Action(SetAvailableRuleSet)
	setAvailableRuleSet(ctx: StateContext<RuleStateModel>, { rulesSets }: SetAvailableRuleSet): any {
		ctx.patchState({
			availableRuleSet: rulesSets
		});
	}

	/**
	 * set the rule set Available for the document
	 * @param ctx
	 * @param rulesSets
	 */
	@Action(OnRevised)
	setOnRevised(ctx: StateContext<RuleStateModel>, { onRevised }: OnRevised): any {
		ctx.patchState({
			onRevised
		});
	}

	/**
	 * get RuleSet available for type of order
	 * @param ctx
	 * @param orderId
	 */
	@Action(SetRuleSet)
	setRuleSet(ctx: StateContext<RuleStateModel>, { orderId }: SetRuleSet): any {
		return this.rulesService.getRulesSets(orderId).pipe(
			tap(response => {
				ctx.patchState({
					rulesSet: response
				});
			})
		);
	}

	/**
	 * set orders rules from server
	 * @param ctx
	 * @param payload
	 */
	@Action(SetOrdersRules)
	setOrdersRules(ctx: StateContext<RuleStateModel>, { payload }: SetOrdersRules): any {
		this.rulesService.setRulesStatusCanSave(DOCUMENT_RULES_UI_SECTIONS.REVISIONS, true, 0);

		return this.rulesService.getOrderDocumentRules(payload.orderId, payload.documentId).pipe(
			tap(
				result => {
					// Process Rules to detects multiple sections
					result = {
						...result,
						violation: this.rulesService.setRuleMultipleSection(result.violation)
					};
					const ruleSaveStatus = this.rulesService.getRulesSaveStatus(result.violation);
					ctx.patchState({
						ordersRules: result,
						ruleSaveStatus,
						rulesLog: [],
						isRunningRules: result.deferredRuleIndicator === RULES_INDICATOR.INPROCESS
					});
					if (payload.revised) {
						this.store.dispatch(new RuleComapateInfo(payload.orderId));
					}
				},
				error => {
					console.error(error);
					ctx.patchState({
						ordersRules: new OrdersRulesModel(),
						ruleSaveStatus: [],
						rulesLog: [],
						isRunningRules: false
					});
				}
			)
		);
	}

	/**
	 * Update Rules after re-execution
	 * @param ctx
	 * @param param1
	 */
	@Action(UpdatedRuleOrders)
	updatedRuleOrders(
		ctx: StateContext<RuleStateModel>,
		{ ordersRules, orderDetailModel }: UpdatedRuleOrders
	): any {;
		this.rulesService.setRulesStatusCanSave(DOCUMENT_RULES_UI_SECTIONS.REVISIONS, true, 0);
		// Process Rules to detects multiple sections
		ordersRules = {
			...ordersRules,
			violation: this.rulesService.setRuleMultipleSection(ordersRules.violation)
		};
		const ruleSaveStatus = this.rulesService.getRulesSaveStatus(ordersRules.violation);

		ctx.patchState({
			ruleSaveStatus,
			ordersRules
		});

		if (orderDetailModel.revised) {
			this.store.dispatch(new RuleComapateInfo(orderDetailModel.id));
		}
	}

	/**
	 * get the rules compare information for a single order
	 * @param ctx
	 * @param orderId
	 */
	@Action(RuleComapateInfo)
	ruleComapateInfo(ctx: StateContext<RuleStateModel>, { orderId }: RuleComapateInfo): any {
		return this.rulesService.getRulesComparerInformation(orderId).pipe(
			tap(result => {
				const state = ctx.getState();
				const rulesCompareInfo = result.violation.map(v => v.name);
				let ordersRules = state.ordersRules;
				const vlt = result.violation.filter(vl => {
					const diff = ordersRules.violation.filter(rc => rc.name === vl.name);
					if (!diff.length) {
						return vl;
					}
				});
				if (vlt.length) {
					this.rulesService.getAdditionalRulesAndUpdateStatus(vlt);
					const violation = ordersRules.violation.concat(vlt);
					ordersRules = {
						...ordersRules,
						violation
					};

				}
				ordersRules = {
					...ordersRules,
					violation: this.rulesService.setRuleMultipleSection(ordersRules.violation)
				};
				const rev = result.violation.filter(r => r.status === RULE_STATUS.NOT_REVIEWED);
				this.rulesService.setRulesStatusCanSave(
					DOCUMENT_RULES_UI_SECTIONS.REVISIONS,
					!rev.length,
					rev.length
				);
				ctx.patchState({
					rulesCompareInfo,
					ordersRules
				});
			})
		);
	}

	/**
	 * Save the current number of rules to be checked
	 * and updated the status wheter we can aproved and order or not
	 * @param ctx
	 * @param payload
	 */
	@Action(SetRulesCheckedRules)
	setRulesCheckedRules(ctx: StateContext<RuleStateModel>, { payload }: SetRulesCheckedRules): any {
		const state = ctx.getState();
		if (state) {
			let newSaveStatus: RuleSaveStatus[] = Object.assign([], state.ruleSaveStatus);
			let ruleStatus = new RuleSaveStatus();
			ruleStatus = payload;
			const hasUIsection = newSaveStatus.filter(r => r.uiSection === payload.uiSection);
			if (!hasUIsection.length) {
				newSaveStatus = newSaveStatus.concat(ruleStatus);
				this.rulesService.updateRuleChecked(ruleStatus.uiSection, ruleStatus.check);
			} else {
				const uiSection = payload.uiSection;
				newSaveStatus = newSaveStatus.map(rs => {
					if (rs.uiSection === uiSection) {
						const canSave = payload.canSave;
						const check = payload.check;
						this.rulesService.updateRuleChecked(uiSection, check);
						return {
							...rs,
							canSave,
							check
						};
					}
					return rs;
				});
			}

			ctx.patchState({
				ruleSaveStatus: newSaveStatus
			});
		}
	}

	/**
	 * set is the rules are running in the background
	 * @param ctx
	 * @param isRunningRules
	 */
	@Action(SetIsRunningRules)
	setIsRunningRules(ctx: StateContext<RuleStateModel>, { isRunningRules }: SetIsRunningRules): any {
		ctx.patchState({
			isRunningRules
		});
	}

	/***
	 * updated rule violation status
	 * @param ctx
	 * @param isRunningRules
	 */
	@Action(UpdatedRuleStatus)
	updatedRuleStatus(ctx: StateContext<RuleStateModel>, { payload }: UpdatedRuleStatus): any {
		const state = ctx.getState();
		const { ordersRules: rule } = state;
		const violation = rule.violation.map(r => {
			if (r.name === payload.ruleName) {
				const status = payload.status;
				return {
					...r,
					status
				};
			}
			return r;
		});
		const ordersRules = {
			...rule,
			violation
		};
		ctx.patchState({
			ordersRules
		});
	}

	/***
	 * Update multiple rules violation status
	 * @param ctx
	 * @param isRunningRules
	 */
	@Action(UpdatedMultipleRuleStatus)
	updatedMultipleRuleStatus(ctx: StateContext<RuleStateModel>, { payload }: UpdatedMultipleRuleStatus): any {
		const state = ctx.getState();

		let ordersRules = Object.assign([], state.ordersRules);
		let violation   = Object.assign([], ordersRules.violation);

		for (const index of Object.keys(payload)) {
			violation = violation.map(rule => {
				if (rule.name === payload[index].ruleName) {
					const status = payload[index].ruleStatus;
					return {
						...rule,
						status
					};
				}
				return rule;
			});
		}

		ordersRules = {
			...ordersRules,
			violation
		};

		ctx.patchState({
			ordersRules
		});

	}

	/**
	 * updated rule violation from external source
	 * @param ctx
	 * @param violation
	 */
	@Action(UpdatedRuleViolation)
	updatedRuleViolation(ctx: StateContext<RuleStateModel>, { payload }: UpdatedRuleViolation): any {
		const state = ctx.getState();
		const { ordersRules: rule } = state;
		const newViolation = Object.assign({}, payload[0]);

		const originalSaveStatus: RuleSaveStatus[] = Object.assign([], state.ruleSaveStatus);
		const v = rule.violation.filter(vl => vl.name === newViolation.name);
		// Process Rule to detect multiple sections
		this.rulesService.setUiSection(newViolation);
		let violation;
		if (v.length) {
			violation = rule.violation.map(vl => {
				if (vl.name === newViolation.name) {
					return (vl = {
						...newViolation
					});
				}
				return vl;
			});
		} else {
			violation = rule.violation.concat(newViolation);
		}

		const saveStatus = originalSaveStatus.map(cs => {
			if (payload[0].uiSection.toUpperCase().includes(cs.uiSection)) {
				const uisRule = violation.filter(vl => vl.uiSection.toUpperCase().includes(cs.uiSection));
				const updateR = uisRule.filter(r => r.status === RULE_STATUS.NOT_REVIEWED);
				const checked = uisRule.filter(r => r.status !== RULE_STATUS.NOT_REVIEWED);
				const canSave = !updateR.length;
				const check = uisRule.length - checked.length;
				this.rulesService.updateRuleChecked(cs.uiSection, check);
				return {
					...cs,
					canSave,
					check
				};
			}
			return cs;
		});

		const ordersRules = {
			...rule,
			violation
		};

		ctx.patchState({
			ordersRules,
			ruleSaveStatus: saveStatus
		});
	}

	/**
	 * add SRR rules for the current orders
	 * @param ctx
	 * @param payload
	 */
	@Action(AddSrrRules)
	AddSrrRules(ctx: StateContext<RuleStateModel>, { payload }: AddSrrRules): any {
		const state = ctx.getState();
		const { ordersRules: rule } = state;
		let violation = rule.violation;

		if (payload.length) {
			payload.forEach(v => {
				// just because i don't trust BK we check if we already have the rules in our side
				const vSrr = rule.violation.filter(viol => viol.name === v.name);
				if (!vSrr.length) {
					const newSrrRule = Object.assign({}, v);
					this.rulesService.setUiSection(newSrrRule);
					violation = [...violation, newSrrRule];
				}
			});

			const ordersRules = {
				...rule,
				violation
			};
			ctx.patchState({
				ordersRules
			});
		}
	}

	/**
	 * add a log for the rule if it was change by another user
	 * @param ctx
	 * @param payload
	 */
	@Action(UpdatedRulelog)
	updatedRulelog(ctx: StateContext<RuleStateModel>, { payload }: UpdatedRulelog): any {
		const state = ctx.getState();
		const rulesLog = state.rulesLog.concat(payload);

		ctx.patchState({
			rulesLog
		});
	}

	/**
	 * clear the ruel notifications data
	 * @param ctx
	 */
	@Action(CleardRulelog)
	cleardRulelog(ctx: StateContext<RuleStateModel>): any {
		const rulesLog = [];
		ctx.patchState({
			rulesLog
		});
	}

	/***
	 * updated rule violation comments
	 * @param ctx
	 * @param isRunningRules
	 */
	@Action(AddComementRule)
	addComementRule(ctx: StateContext<RuleStateModel>, { payload }: AddComementRule): any {
		const state = ctx.getState();
		const { ordersRules: rule } = state;
		const violation = rule.violation.map(r => {
			if (r.name === payload.ruleName) {
				const notes = r.notes.concat(payload.notes);
				return {
					...r,
					notes
				};
			}
			return r;
		});

		const ordersRules = {
			...rule,
			violation
		};

		ctx.patchState({
			ordersRules
		});
	}

	/***
	 * updated rule violation stips
	 * @param ctx
	 * @param isRunningRules
	 */
	@Action(AddStipRule)
	addStipRule(ctx: StateContext<RuleStateModel>, { payload }: AddStipRule): any {
		const state = ctx.getState();
		const { ordersRules: rule } = state;
		const violation = rule.violation.map(r => {
			if (r.name === payload.ruleName) {
				const stips = r.stips.concat(payload.stips);
				return {
					...r,
					stips
				};
			}
			return r;
		});

		const ordersRules = {
			...rule,
			violation
		};
		ctx.patchState({
			ordersRules
		});
	}

	/**
	 * add a manual rule into the list of the rules
	 * @param ctx
	 * @param payload
	 */
	@Action(AddManualRule)
	addManualRule(ctx: StateContext<RuleStateModel>, { payload }: AddManualRule): any {
		const state = ctx.getState();
		const { ordersRules: rule } = state;
		const newViolation = payload.violation;
		// Process Rule to detect multiple sections
		this.rulesService.setUiSection(newViolation);

		const violation = [newViolation].concat(rule.violation);

		const ordersRules = {
			...rule,
			violation
		};
		ctx.patchState({
			ordersRules
		});
	}

	/**
	 * delete note from rule violation
	 * @param ctx
	 * @param payload
	 */
	@Action(DeleteComementRule)
	deleteComementRule(ctx: StateContext<RuleStateModel>, { payload }: DeleteComementRule): any {
		const state = ctx.getState();
		const { deletedNotes, ruleName } = payload;
		const { ordersRules: rule } = state;
		const violation = rule.violation.map(v => {
			if (v.name === ruleName) {
				const notes = v.notes.filter(n => deletedNotes.every(dn => dn.id !== n.id));
				return {
					...v,
					notes
				};
			}
			return v;
		});

		const ordersRules = {
			...rule,
			violation
		};

		ctx.patchState({
			ordersRules
		});
	}

	/**
	 * delete stips from rule violation
	 * @param ctx
	 * @param payload
	 */
	@Action(DeleteStipsRule)
	deleteStipsRule(ctx: StateContext<RuleStateModel>, { payload }: DeleteStipsRule): any {
		const state = ctx.getState();
		const { deletedStips, ruleName } = payload;
		const { ordersRules: rule } = state;
		const violation = rule.violation.map(v => {
			if (v.name === ruleName) {
				const stips = v.stips.filter(n => deletedStips.every(dn => dn.id !== n.id));
				return {
					...v,
					stips
				};
			}
			return v;
		});

		const ordersRules = {
			...rule,
			violation
		};

		ctx.patchState({
			ordersRules
		});
	}

	/**
	 * set uncheckedrules selected option
	 * @param ctx
	 * @param uncheckedRules
	 */
	@Action(SetUncheckedRules)
	setUncheckedRules(ctx: StateContext<RuleStateModel>, { uncheckedRules }: SetUncheckedRules): any {
		ctx.patchState({
			uncheckedRules
		});
	}

	/**
	 * Every time a Rule is Selected we keep the object so we can use to highlight the Item on the UI
	 * @param ctx
	 * @param selectedRule
	 */
	@Action(SetSelectedRule)
	setSelectedRule(ctx: StateContext<RuleStateModel>, { selectedRule }: SetSelectedRule): any {
		ctx.patchState({
			selectedRule
		});
	}

	/**
	 * Delete the Last Selected Rule or null the value
	 * @param ctx
	 * @param selectedRule
	 */
	@Action(DeleteSelectedRule)
	deleteSelectedRule(ctx: StateContext<RuleStateModel>): any {
		ctx.patchState({
			selectedRule: null
		});
	}

	/**
	 * Logout.
	 * @param ctx: InboxStateModel
	 */
	@Action(Logout)
	logout(ctx: StateContext<RuleStateModel>): void {
		ctx.setState(new RuleStateModel());
	}

	/**
	 * ClearRules.
	 * @param ctx: RuleStateModel
	 */
	@Action(ClearRules)
	clearRules(ctx: StateContext<RuleStateModel>): void {
		ctx.setState(new RuleStateModel());
	}
}
